import { createClient } from '@supabase/supabase-js';

// Define types from your database schema
export type RepoScan = {
  id: string;
  user_id: string;
  repo_url: string;
  pull_request_id: string | null;
  scan_date: string;
  scan_status: string;
  results: Record<string, unknown> | null;
  created_at: string;
};

export type User = {
  id: string;
  email: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  github_id: string | null;
  github_installation_id: string | null;
  created_at: string;
  updated_at: string;
  subscription_tier: string;
  total_repo_scans: number;
  last_active: string | null;
  stripe_customer_id: string | null;
  subscription_id: string | null;
  subscription_status: string | null;
};

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Check if the configuration is available
if (!supabaseUrl) {
  console.error('Supabase URL must be provided in environment variables');
}
if (!supabaseKey) {
  console.error('Supabase anon key must be provided in environment variables');
}

// Create a Supabase client
export const supabase = createClient(supabaseUrl, supabaseKey);

// Helper function to create a client with specific credentials
export const createSupabaseClient = (url: string, key: string) => {
  return createClient(url, key);
};

// Type-safe database methods
export const supabaseDB = {
  // User methods
  async getCurrentUser() {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data as UserProfile;
  },

  // Repository scan methods
  async getUserScans(userId: string, limit = 10) {
    const { data, error } = await supabase
      .from('repo_scans')
      .select('*')
      .eq('user_id', userId)
      .order('scan_date', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data as RepoScan[];
  },

  // Dashboard stats
  async getDashboardStats(userId: string) {
    // This would typically be an API call or RPC function in Supabase
    const { data, error } = await supabase
      .rpc('get_dashboard_stats', { user_id: userId });

    if (error) throw error;
    return data;
  }
};

// Auth helper functions
export const auth = {
  login: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  },

  register: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });

    if (error) throw error;
    return data;
  },

  logout: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  resetPassword: async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    if (error) throw error;
  }
};

// Type definitions for the database schema
export type UserProfile = {
  id: string;
  username: string;
  email: string;
  full_name: string;
  avatar_url: string;
  github_id: string | null;
  github_installation_id: string | null;
  plan: 'free' | 'pro' | 'team' | 'enterprise';
  is_active: boolean;
  stripe_customer_id: string | null;
  subscription_id: string | null;
  subscription_status: string | null;
  subscription_tier: string | null;
  total_repo_scans: number;
  trial_end: string | null;
  reviews_count: number;
  last_active: string | null;
  created_at: string;
  updated_at: string;
};

export type RepoInstall = {
  id: number;
  user_id: string;
  repository_id: string;
  repository_name: string;
  repository_owner: string;
  installation_id: string;
  review_count: number;
  last_review_at: string | null;
  installed_at: string;
  updated_at: string;
};
